import { useState } from 'react';
import { Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AnchorHeadingProps {
  level: 2 | 3 | 4;
  id: string;
  children: React.ReactNode;
  className?: string;
}

export function AnchorHeading({ level, id, children, className }: AnchorHeadingProps) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    // Context-aware: works on any domain (preview, lovable, custom)
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const Tag = `h${level}` as 'h2' | 'h3' | 'h4';
  
  const sizeClasses = {
    2: 'text-2xl font-bold',
    3: 'text-xl font-semibold',
    4: 'text-lg font-medium',
  };
  
  return (
    <Tag 
      id={id} 
      className={cn(
        'group flex items-center gap-2 scroll-mt-24',
        sizeClasses[level],
        className
      )}
    >
      {children}
      <button
        onClick={handleCopy}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
        aria-label="Copy link to section"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Link2 className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
    </Tag>
  );
}
